/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-lonely-if */

import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Pagination from '@mui/material/Pagination';
import { useHistory } from 'react-router-dom';
import LensRow from './LensRow';
import ScreeningProgressNote from './ScreeningProgressNote';
import { IEntityTicketReportData } from 'components/Common/EntityTicketPdf';
import Level1Headers from 'components/Monitor/ResultsTab/Table/Level1Headers';
import Level1Row from 'components/Monitor/ResultsTab/Table/Level1Row';
import Level3Headers from 'components/Monitor/ResultsTab/Table/Level3Headers';
import Level3Row from 'components/Monitor/ResultsTab/Table/Level3Row';
import VersionModal from 'components/Monitor/Modals/VersionModal';
import LensColumns from './LensColumns';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import EntityResultsDownloadModal from '../Modals/EntityResultsDownloadModal';
import EntityHitsFilterModal from '../Modal/EntityHitsFilterModal';
import BulkDismissHitsModal from '../Modal/BulkDismissHitsModal';
import {
  BulkHitSelection,
  BulkHitVisibilityOperation,
  getBulkHitSelectionKey,
} from './bulkHitSelection';
import {
  EMPTY_ENTITY_HIT_FILTERS,
  EntityHitSortKey,
  SortDirection,
  collectHitFilterOptions,
  filterEntityHits,
  getEntityHitDocs,
  hasActiveHitFilters,
  sortEntityHits,
} from './entityHitList';

interface ScreeningProgress {
  chunksProcessed: number;
  chunksTotal: number;
  progressPercentage: number;
}

interface TableType {
  tab: string;
  entity: string;
  cols: React.ReactNode;
  suppliersData?: any;
  selectedItems: any;
  setSelectedItems: any;
  curVersion: any;
  passedDownResults: any;
  activeFilter: string;
  curLevel: number;
  setAISums: any;
  whitelistEntity?: (content: any) => void;
  removeHit: any;
  setCurLevel: any;
  setIsHistoryModalOpen: any;
  handleVisClick: any;
  cur_case: any;
  setShareModal: any;
  isBulkTicket: boolean;
  setIsCommentsModalOpen: any;
  setIsFrequencyModalOpen: any;
  setIsApprovalModalOpen: any;
  setScreenParamsModal: any;
  handleCSV: any;
  updateFilters: any;
  resetTable: any;
  newTab: boolean;
  setNewTab: any;
  currentPage: number;
  setCurrentPage: any;
  currentItems: any;
  setCurrentItems: any;
  removeTrigger: boolean;
  isLoading: boolean;
  isScreeningInProgress?: boolean;
  isProduct?: boolean;
  screeningProgress?: ScreeningProgress | null;
  databaseId?: string;
  groupId?: string;
  entityIdForHitsReport?: string;
  /** Passed straight through to EntityResultsDownloadModal — see its prop docs. */
  buildEntityScreeningReport?: () => IEntityTicketReportData | null;
  canAccessVerificationResults?: boolean;
  hideTableToolbar?: boolean;
  externalSearchQuery?: string;
  bulkSelectedHits?: Map<string, BulkHitSelection>;
  setBulkSelectedHits?: React.Dispatch<
    React.SetStateAction<Map<string, BulkHitSelection>>
  >;
  parentDatabaseEntityId?: string;
  parentEntityName?: string;
  onBulkUpdateHits?: (
    operation: BulkHitVisibilityOperation,
    comment: string,
  ) => Promise<void>;
  isBulkHiding?: boolean;
  removedHitIds?: string[];
}

const EMPTY_BULK_HIT_SELECTIONS = new Map<string, BulkHitSelection>();
const EMPTY_REMOVED_HIT_IDS: string[] = [];

const applyHitVisibility = (
  hits: any[] = [],
  removedHitIds: Set<string>,
): any[] => {
  const visibleHits: any[] = [];
  const hiddenHits: any[] = [];

  hits.forEach((hit) => {
    const hitId = String(hit?.['BITE Record ID'] ?? hit?.id ?? '');
    const isRemoved = Boolean(hitId && removedHitIds.has(hitId));
    const projectedHit =
      Boolean(hit?.removed) === isRemoved
        ? hit
        : { ...hit, removed: isRemoved };

    if (isRemoved) {
      hiddenHits.push(projectedHit);
    } else {
      visibleHits.push(projectedHit);
    }
  });

  return [ ...visibleHits, ...hiddenHits ];
};

const LensesTable: React.FC<TableType> = ({
  tab,
  entity,
  cols,
  suppliersData,
  selectedItems,
  setSelectedItems,
  curVersion,
  passedDownResults,
  activeFilter,
  isBulkTicket,
  whitelistEntity,
  removeHit,
  curLevel,
  setCurLevel,
  setIsHistoryModalOpen,
  setAISums,
  handleVisClick,
  cur_case,
  setShareModal,
  setIsApprovalModalOpen,
  setIsCommentsModalOpen,
  setIsFrequencyModalOpen,
  setScreenParamsModal,
  handleCSV,
  updateFilters,
  resetTable,
  newTab,
  setNewTab,
  currentPage,
  setCurrentPage,
  currentItems,
  setCurrentItems,
  removeTrigger,
  isLoading,
  isScreeningInProgress = false,
  isProduct = false,
  screeningProgress,
  databaseId,
  groupId,
  entityIdForHitsReport,
  buildEntityScreeningReport,
  canAccessVerificationResults = true,
  hideTableToolbar = false,
  externalSearchQuery,
  bulkSelectedHits = EMPTY_BULK_HIT_SELECTIONS,
  setBulkSelectedHits,
  parentDatabaseEntityId,
  parentEntityName,
  onBulkUpdateHits,
  isBulkHiding = false,
  removedHitIds = EMPTY_REMOVED_HIT_IDS,
}) => {
  const history = useHistory();
  const [isOpenVersionModal, setIsOpenVersionModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>({});
  const [selectedFilteredResults, setSelectedFilteredResults] = useState<any>(
    [],
  );
  const [selectedFilter, setSelectedFilter] = useState('');
  const [filteredResArr, setFilteredResArr] = useState<any>({});
  const [totalPages, setTotalPages] = useState(1);
  const [indexOfFirstItems, setIndexOfFirstItems] = useState(0);
  const [isResultsDownloadModalOpen, setIsResultsDownloadModalOpen] =
    useState(false);
  const [isBulkReviewOpen, setIsBulkReviewOpen] = useState(false);
  const [isHitsFilterOpen, setIsHitsFilterOpen] = useState(false);
  const [hitFilters, setHitFilters] = useState(EMPTY_ENTITY_HIT_FILTERS);
  const [hitSortKey, setHitSortKey] = useState<EntityHitSortKey>('matchScore');
  const [hitSortDirection, setHitSortDirection] =
    useState<SortDirection>('desc');
  const entityHitsView = !isProduct;
  const itemsPerPage = entityHitsView ? 100 : 10;
  const showBulkSelection = Boolean(
    !isProduct && parentDatabaseEntityId && setBulkSelectedHits,
  );
  const removedHitIdSet = useMemo(
    () => new Set(removedHitIds.map((id) => String(id))),
    [removedHitIds],
  );
  const hasVerificationResults = Boolean(
    suppliersData?.website_quality_info ||
      suppliersData?.places_verification_info ||
      suppliersData?.cbr_registry_info ||
      suppliersData?.domain_age_info ||
      suppliersData?.domain_reputation_info ||
      suppliersData?.email_verify_info ||
      suppliersData?.phone_verify_info,
  );

  const handleViewReports = () => {
    if (!databaseId) {
      return;
    }

    setIsResultsDownloadModalOpen(false);
    history.push(`/database/${databaseId}?tab=Generated%20Reports&page=1`);
  };

  const handlePageChange = (e: ChangeEvent<unknown>, new_page: number) => {
    setCurrentPage(new_page);
    if (entityHitsView) {
      return;
    }
    const indexOfLastItem = new_page * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setIndexOfFirstItems(indexOfFirstItem);

    if (curLevel === 1) {
      setCurrentItems(
        passedDownResults?.slice(indexOfFirstItem, indexOfLastItem),
      );
    } else if (curLevel === 2) {
      const newArr = groupByKey(
        passedDownResults[0]?.docs,
        activeFilter ? activeFilter : '',
      );
      setCurrentItems(newArr?.slice(indexOfFirstItem, indexOfLastItem));
    } else {
      setCurrentItems(
        applyHitVisibility(
          selectedFilteredResults,
          removedHitIdSet,
        ).slice(indexOfFirstItem, indexOfLastItem),
      );
    }
  };

  const handleVersionCloseModal = () => {
    setIsOpenVersionModal(!isOpenVersionModal);
  };

  const handleHitSort = (key: EntityHitSortKey) => {
    if (hitSortKey === key) {
      setHitSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
    } else {
      setHitSortKey(key);
      setHitSortDirection(
        key === 'matchScore' || key === 'dateAdded' ? 'desc' : 'asc',
      );
    }
    setCurrentPage(1);
  };

  const hitFilterOptions = useMemo(
    () => collectHitFilterOptions(getEntityHitDocs(passedDownResults, entity)),
    [passedDownResults, entity],
  );

  const handleHitSelect = (hit: any) => {
    setSelectedResult(hit);
    const newArr = groupByKey(hit?.docs, activeFilter ? activeFilter : '');
    setFilteredResArr(newArr);
    setCurLevel(2);
    setCurrentPage(1);
    const indexOfLastItem = (currentPage ? currentPage : 1) * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setIndexOfFirstItems(indexOfFirstItem);
    setTotalPages(Math.ceil(newArr?.length / itemsPerPage));
    setCurrentItems(newArr.slice(indexOfFirstItem, indexOfLastItem));
  };

  // useEffect(() => {
  //   if (curLevel && curLevel > 1) {
  //     const entityName = selectedResult.field;
  //     if (entityName && cur_case.data.aiSums) {
  //       const aiSumsData = cur_case.data.aiSums.filter((aiSum: any) => aiSum.entity_name === entityName);
  //       setAISums(Array.isArray(aiSumsData) ? aiSumsData : [ aiSumsData ]);
  //     }
  //   }
  // }, [ curLevel, selectedResult ]);

  const handleFilteredHitSelect = (filteredHit: any, filter: string) => {
    setSelectedFilteredResults(filteredHit);
    setSelectedFilter(filter);
    setCurLevel(3);
    setCurrentPage(1);
    const indexOfLastItem = (currentPage ? currentPage : 1) * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setIndexOfFirstItems(indexOfFirstItem);
    setTotalPages(Math.ceil(filteredHit?.length / itemsPerPage));
    setCurrentItems(filteredHit.slice(indexOfFirstItem, indexOfLastItem));
  };

  const groupByKey = (array: any[] = [], key: string) => {
    const groupsMap = (array || []).reduce((acc, obj) => {
      const keyValue = obj[key];
      if (keyValue !== undefined && keyValue !== null) {
        if (!acc[keyValue]) {
          acc[keyValue] = [];
        }
        acc[keyValue].push(obj);
      } else {
        const missingKey = `No ${key}`;
        if (!acc[missingKey]) {
          acc[missingKey] = [];
        }
        acc[missingKey].push(obj);
      }
      return acc;
    }, {} as Record<string, any[]>);

    const result = Object.entries(groupsMap).map(([groupKey, items]) => ({
      key: groupKey,
      items: items,
    }));

    return result;
  };

  const markAndMoveRemoved = (arr: any) => {
    const remaining: any = [];
    const removed = [];

    for (const item of arr) {
      if (cur_case.data.removedHits?.includes(item['BITE Record ID'])) {
        removed.push({ ...item, removed: true });
      } else {
        remaining.push(item);
      }
    }

    return [...remaining, ...removed];
  };

  const sumHits = (objects: any[]): number => {
    return objects
      .filter(
        (item: { field: string }) =>
          item.field.toLowerCase().trim() === `${entity}`.toLowerCase(),
      )
      .reduce((total, obj) => total + (obj.hits || 0), 0);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const resolvedSearchQuery = externalSearchQuery ?? searchQuery;

  const handleSearchChange = (e: any) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const getHitId = (hit: any): string =>
    String(hit?.['BITE Record ID'] ?? hit?.id ?? '');

  const getHitName = (hit: any): string =>
    String(hit?.['Name of Entity'] ?? hit?.['Product Name'] ?? 'Unnamed hit');

  const toBulkSelection = (hit: any): BulkHitSelection | null => {
    if (!parentDatabaseEntityId) {
      return null;
    }
    const hitId = getHitId(hit);
    if (!hitId) {
      return null;
    }

    return {
      key: getBulkHitSelectionKey(parentDatabaseEntityId, hitId),
      databaseEntityId: parentDatabaseEntityId,
      entityName: parentEntityName || entity || 'Entity',
      hitId: hitId,
      hitName: getHitName(hit),
      operation: hit?.removed ? 'unhide' : 'hide',
    };
  };

  const bulkSelections = useMemo(
    () => Array.from(bulkSelectedHits.values()),
    [bulkSelectedHits],
  );
  const bulkOperation = bulkSelections[0]?.operation;
  const pageSelectionOperation: BulkHitVisibilityOperation =
    bulkOperation ||
    ((currentItems || []).some((hit: any) => !hit?.removed)
      ? 'hide'
      : 'unhide');
  const selectablePageHits = useMemo(
    () =>
      entityHitsView || curLevel === 3
        ? (currentItems || [])
            .map(toBulkSelection)
            .filter(
              (selection: BulkHitSelection | null): selection is BulkHitSelection =>
                Boolean(
                  selection &&
                    selection.operation === pageSelectionOperation,
                ),
            )
        : [],
    [
      curLevel,
      entityHitsView,
      currentItems,
      parentDatabaseEntityId,
      parentEntityName,
      entity,
      pageSelectionOperation,
    ],
  );
  const selectedPageHitCount = selectablePageHits.reduce(
    (count: number, selection: {key: string}) =>
      count + (bulkSelectedHits.has(selection.key) ? 1 : 0),
    0,
  );
  const isPageSelected =
    selectablePageHits.length > 0 &&
    selectedPageHitCount === selectablePageHits.length;
  const isPageIndeterminate =
    selectedPageHitCount > 0 && !isPageSelected;
  const selectedEntityCount = useMemo(
    () =>
      new Set(
        bulkSelections.map((selection) => selection.databaseEntityId),
      ).size,
    [bulkSelections],
  );

  const handleToggleBulkHit = (hit: any) => {
    const selection = toBulkSelection(hit);
    if (!selection || !setBulkSelectedHits) {
      return;
    }

    setBulkSelectedHits((previous) => {
      const next = new Map(previous);
      if (next.has(selection.key)) {
        next.delete(selection.key);
      } else if (
        next.size > 0 &&
        next.values().next().value?.operation !== selection.operation
      ) {
        return previous;
      } else {
        next.set(selection.key, selection);
      }
      return next;
    });
  };

  const handleTogglePageSelection = () => {
    if (!setBulkSelectedHits || selectablePageHits.length === 0) {
      return;
    }

    setBulkSelectedHits((previous) => {
      const next = new Map(previous);
      if (isPageSelected) {
        selectablePageHits.forEach((selection: { key: string}) => next.delete(selection.key));
      } else {
        selectablePageHits.forEach((selection: BulkHitSelection) =>
          next.set(selection.key, selection),
        );
      }
      return next;
    });
  };

  const handleRemoveBulkSelection = (key: string) => {
    setBulkSelectedHits?.((previous) => {
      const next = new Map(previous);
      next.delete(key);
      return next;
    });
  };

  const handleClearBulkSelection = () => {
    setBulkSelectedHits?.(new Map());
  };

  const handleConfirmBulkUpdate = async (comment: string) => {
    if (!onBulkUpdateHits || !bulkOperation) {
      return;
    }
    await onBulkUpdateHits(bulkOperation, comment);
    setIsBulkReviewOpen(false);
  };

  useEffect(() => {
    if (
      !entityHitsView &&
      curLevel === 3 &&
      (!selectedFilteredResults || selectedFilteredResults.length === 0)
    ) {
      setCurLevel(2);
      setCurrentPage(1);
    }
  }, []);

  useEffect(() => {
    let baseData: any[] = [];

    if (entityHitsView) {
      const docs = applyHitVisibility(
        getEntityHitDocs(passedDownResults, entity),
        removedHitIdSet,
      );
      setSelectedResult({ field: entity, docs: docs });
      baseData = sortEntityHits({
        hits: filterEntityHits({
          hits: docs,
          filters: hitFilters,
          searchQuery: resolvedSearchQuery,
          matchTerm: entity,
        }),
        sortKey: hitSortKey,
        sortDirection: hitSortDirection,
        matchTerm: entity,
      });
    } else if (curLevel === 1) {
      if (passedDownResults?.length > 0) {
        baseData = passedDownResults.filter(
          (item: { field: string }) =>
            item.field.toLowerCase().trim() === `${entity}`.toLowerCase(),
        );
      }
    } else if (curLevel === 2) {
      if (passedDownResults?.length > 0) {
        passedDownResults.map((item: { field: any }) => item.field);
        const filtered = passedDownResults.filter(
          (item: { field: string }) =>
            item.field?.toLowerCase().trim() === `${entity}`.toLowerCase(),
        );
        setSelectedResult(filtered[0]);
        baseData = groupByKey(
          filtered[0]?.docs,
          activeFilter ? activeFilter : '',
        );
        setFilteredResArr(baseData);
      }
    } else {
      baseData = applyHitVisibility(
        selectedFilteredResults,
        removedHitIdSet,
      );
    }

    if (!entityHitsView && resolvedSearchQuery) {
      baseData = baseData.filter((item: any) =>
        Object.values(item).some(
          (value) =>
            typeof value === 'string' &&
            value.toLowerCase().includes(resolvedSearchQuery.toLowerCase()),
        ),
      );
    }

    const indexOfLastItem = (currentPage ? currentPage : 1) * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setIndexOfFirstItems(indexOfFirstItem);
    setTotalPages(Math.ceil((baseData?.length || 0) / itemsPerPage) || 1);
    setCurrentItems(baseData?.slice(indexOfFirstItem, indexOfLastItem) || []);
  }, [
    passedDownResults,
    curLevel,
    currentPage,
    itemsPerPage,
    activeFilter,
    resolvedSearchQuery,
    removedHitIdSet,
    entityHitsView,
    entity,
    hitFilters,
    hitSortKey,
    hitSortDirection,
  ]);

  // useEffect(() => {
  //   const indexOfLastItem = (currentPage ? currentPage : 1) * itemsPerPage;
  //   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  //   setIndexOfFirstItems(indexOfFirstItem);
  //   setTotalPages(Math.ceil(selectedFilteredResults?.length / itemsPerPage));
  //   // setCurrentItems(selectedFilteredResults.slice(indexOfFirstItem, indexOfLastItem));
  // }, [ removeTrigger ]);

  return (
    <div style={{ width: '100%', position: 'relative', zIndex: 0 }}>
      <div className='screening-blur-container' style={{ width: '100%' }}>
        <div
          className='results-table-frame'
          style={{ width: '100%', marginBottom: '20px' }}
        >
          <div className='results-table-frame-headers'>
            <div className='results-table-frame-headers-left'>
              {entityHitsView ? (
                <div className='results-table-frame-headers-left-all'>
                  <div className='results-table-frame-headers-left-all-crumb'>
                    {entity || 'All Matches'}
                  </div>
                </div>
              ) : (
                <>
              <div
                className='results-table-frame-headers-left-all'
                onClick={() => {
                  setCurLevel(1);
                  setCurrentPage(1);
                }}
              >
                <div className='results-table-frame-headers-left-all-crumb'>
                  All
                </div>
              </div>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M6 12L10 8L6 4'
                  stroke='var(--bread-crumbs)'
                  strokeWidth='1.33333'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              {curLevel && curLevel >= 2 && (
                <>
                  <div
                    className='results-table-frame-headers-left-all'
                    onClick={() => {
                      setCurLevel(2);
                      setCurrentPage(1);
                    }}
                  >
                    <div className='results-table-frame-headers-left-all-crumb'>
                      {selectedResult?.field}
                    </div>
                  </div>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M6 12L10 8L6 4'
                      stroke='var(--bread-crumbs)'
                      strokeWidth='1.33333'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </>
              )}
              {curLevel && curLevel >= 3 && (
                <div className='results-table-frame-headers-left-all'>
                  <div className='results-table-frame-headers-left-all-crumb'>
                    {selectedFilter}
                  </div>
                </div>
              )}
                </>
              )}
            </div>
            <div className='results-table-frame-headers-right'>
              {cur_case?.data?.screeningDate && (
                <div className='results-table-frame-headers-left-all'>
                  <div className='results-table-frame-headers-left-all-crumb'>
                    Last Screen:{' '}
                    {new Date(cur_case.data.screeningDate).toLocaleString(
                      'en-US',
                      {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'UTC',
                      },
                    )}{' '}
                    UTC
                  </div>
                </div>
              )}
              {entityHitsView && (
                <button
                  type='button'
                  className='screening-btn'
                  style={{
                    marginLeft: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    borderColor: hasActiveHitFilters(hitFilters)
                      ? 'var(--action-color)'
                      : undefined,
                  }}
                  onClick={() => setIsHitsFilterOpen(true)}
                >
                  <FilterListIcon style={{ fontSize: 16 }} />
                  Filters
                </button>
              )}
              {!isProduct && (
                <button
                  type='button'
                  className='screening-btn'
                  style={{
                    marginLeft: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  onClick={() => setIsResultsDownloadModalOpen(true)}
                  disabled={isLoading}
                >
                  <DownloadOutlinedIcon style={{ fontSize: 16 }} />
                  Download Results
                </button>
              )}
            </div>
          </div>
          <div className='results-table-frame-tables'>
            <div className='results-table-frame-tables-table'>
              {/* This row keeps a hard height (77px) from its class, so when a
                  host page supplies its own search/tabs and passes
                  hideTableToolbar (see BiteDBEntityScreeningResults), hiding the
                  contents alone left an empty band between the breadcrumb and
                  the column headers. Collapse the row in that case. */}
              <div
                className={`results-table-frame-tables-table-content${
                  hideTableToolbar ? ' is-toolbar-hidden' : ''
                }`}
              >
                {!hideTableToolbar && (newTab ? (
                  <div className='results-table-frame-tables-table-content-hor'>
                    <div
                      className='results-table-frame-tables-table-content-hor-detail'
                      onClick={() => setNewTab(false)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='results-table-frame-tables-table-content-hor-detail-text'>
                        All
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='results-table-frame-tables-table-content-hor'>
                    <div className='results-table-frame-tables-table-content-hor-all'>
                      <div className='results-table-frame-tables-table-content-hor-all-text'>
                        All
                      </div>
                    </div>
                  </div>
                ))}
                {!hideTableToolbar && <div className='monitor-table-results-display'>
                  Results:{' '}
                  {passedDownResults?.length > 0
                    ? sumHits(passedDownResults)
                    : '0'}
                </div>}
                {!hideTableToolbar && <div className='results-table-frame-tables-table-content-search'>
                  <div className='tabs-filters-input-dropdown'>
                    <div style={{ maxHeight: '40px' }}>
                      <div
                        className='input-icon-container'
                        style={{ top: '12px' }}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='20'
                          height='20'
                          viewBox='0 0 20 20'
                          fill='none'
                        >
                          <path
                            id='Icon'
                            d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
                            stroke='var(--card-text)'
                            strokeWidth='1.66667'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                      <input
                        className='tabs-filters-input'
                        placeholder='Search results'
                        style={{ maxHeight: '40px' }}
                        onChange={(e) => handleSearchChange(e)}
                      />
                    </div>
                  </div>
                  {/* <svg xmlns='http://www.w3.org/2000/svg' width='18' height='19' viewBox='0 0 18 19' fill='none' cursor={'pointer'} onClick={handleVersionCloseModal}>
                    <path d='M2.25 9.5H15.75M2.25 5H15.75M6.75 14H15.75' stroke='var(--card-text)' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                  </svg> */}
                </div>}
              </div>
              {isLoading ? (
                <div className='view-history-head-frame-table'>
                  <div
                    className='view-history-body-frame'
                    style={{
                      height: 'fit-content',
                      maxHeight: '450px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      <CircularProgress color='primary' />
                    </div>
                    <p>Loading...</p>
                  </div>
                </div>
              ) : Object.keys(passedDownResults ? passedDownResults : [])
                  ?.length === 0 ? (
                <div className='view-history-head-frame-table'>
                  <Level1Headers />
                  <div
                    className='view-history-body-frame'
                    style={{ height: 'fit-content', maxHeight: '450px' }}
                  >
                    <p>No Results for this Item</p>
                  </div>
                </div>
              ) : curLevel === 1 ? (
                <div className='view-history-head-frame-table'>
                  <Level1Headers />
                  <div
                    className='view-history-body-frame'
                    style={{ height: 'fit-content', maxHeight: '450px' }}
                  >
                    {currentItems?.map((row: any, index: number) => {
                      return (
                        <Level1Row
                          key={index}
                          index={indexOfFirstItems + index}
                          result={row}
                          handleHitSelect={handleHitSelect}
                          handleVisClick={handleVisClick}
                          handleWhitelistClick={
                            whitelistEntity
                              ? whitelistEntity
                              : () => {
                                  return;
                                }
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              ) : !entityHitsView && curLevel === 2 ? (
                <div className='view-history-head-frame-table'>
                  <LensColumns />
                  <div
                    className='view-history-body-frame'
                    style={{ height: 'fit-content', maxHeight: '450px' }}
                  >
                    {currentItems?.map((row: any, index: number) => (
                      <LensRow
                        key={index}
                        results={row.items}
                        name={row.key}
                        handleHitSelect={handleFilteredHitSelect}
                        selectedItems={selectedItems}
                        setSelectedItems={setSelectedItems}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className='view-history-head-frame-table'>
                  <Level3Headers
                    isProduct={isProduct}
                    showMatchScore={!isProduct}
                    showBulkSelection={showBulkSelection}
                    isPageSelected={isPageSelected}
                    isPageIndeterminate={isPageIndeterminate}
                    onTogglePageSelection={handleTogglePageSelection}
                    bulkOperation={pageSelectionOperation}
                    sortKey={entityHitsView ? hitSortKey : undefined}
                    sortDirection={hitSortDirection}
                    onSort={entityHitsView ? handleHitSort : undefined}
                    layout={entityHitsView ? 'entityHits' : 'default'}
                  />
                  <div
                    className='view-history-body-frame'
                    style={{ height: 'fit-content', maxHeight: '450px' }}
                  >
                    {currentItems?.map((row: any, index: number) => (
                      <Level3Row
                        key={index}
                        index={index}
                        result={row}
                        selected={selectedResult.field}
                        handleVisClick={removeHit}
                        isProduct={isProduct}
                        showMatchScore={!isProduct}
                        matchTerm={entity}
                        showBulkSelection={showBulkSelection}
                        isBulkSelected={bulkSelectedHits.has(
                          getBulkHitSelectionKey(
                            parentDatabaseEntityId || '',
                            getHitId(row),
                          ),
                        )}
                        onToggleBulkSelection={handleToggleBulkHit}
                        isBulkSelectionDisabled={Boolean(
                          bulkOperation &&
                            bulkOperation !==
                              (row?.removed ? 'unhide' : 'hide'),
                        )}
                        layout={entityHitsView ? 'entityHits' : 'default'}
                      />
                    ))}
                  </div>
                </div>
              )}
              {bulkSelections.length > 0 && (
                <div className='bulk-hit-action-tray' role='status'>
                  <div className='bulk-hit-action-tray__summary'>
                    <strong>{bulkSelections.length}</strong>
                    <span>
                      hit{bulkSelections.length === 1 ? '' : 's'} selected across{' '}
                      {selectedEntityCount} entit
                      {selectedEntityCount === 1 ? 'y' : 'ies'} to{' '}
                      {bulkOperation}
                    </span>
                  </div>
                  <div className='bulk-hit-action-tray__actions'>
                    <button
                      type='button'
                      className='bulk-hit-action-tray__clear'
                      onClick={handleClearBulkSelection}
                      disabled={isBulkHiding}
                    >
                      Clear
                    </button>
                    <button
                      type='button'
                      className='bulk-hit-action-tray__review'
                      onClick={() => setIsBulkReviewOpen(true)}
                      disabled={isBulkHiding}
                    >
                      Review &amp; {bulkOperation}
                    </button>
                  </div>
                </div>
              )}
              <div className='monitor-results-footer'>
                <div className='screening-results-pagination-text'>
                  Page {currentPage} of {totalPages}
                </div>
                <div className='screening-results-footer-action-group'>
                  <div className='hb-pagination'>
                    <Pagination
                      onChange={handlePageChange}
                      size='large'
                      count={totalPages}
                      shape='rounded'
                      variant='text'
                      page={currentPage}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isOpenVersionModal && (
            <VersionModal
              cur_case={cur_case}
              setShareModal={setShareModal}
              setIsCommentsModalOpen={setIsCommentsModalOpen}
              setIsFrequencyModalOpen={setIsFrequencyModalOpen}
              setIsApprovalModalOpen={setIsApprovalModalOpen}
              setIsHistoryModalOpen={setIsHistoryModalOpen}
              setScreenParamsModal={setScreenParamsModal}
              handleCSV={handleCSV}
            />
          )}
        </div>
      </div>

      <div
        className={`screening-overlay ${
          isScreeningInProgress ? 'visible' : ''
        }`}
      >
        <div
          className='screening-message'
          style={{
            background: 'var(--Background-200, var(--bg-colors))',
            padding: '24px',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <h3>Screen in Progress...</h3>
          <ScreeningProgressNote progress={screeningProgress} />
        </div>
      </div>
      <EntityHitsFilterModal
        open={isHitsFilterOpen}
        onClose={() => setIsHitsFilterOpen(false)}
        filters={hitFilters}
        lensOptions={hitFilterOptions.lenses}
        listNameOptions={hitFilterOptions.listNames}
        entityCountryOptions={hitFilterOptions.entityCountries}
        onApply={(nextFilters) => {
          setHitFilters(nextFilters);
          setCurrentPage(1);
        }}
      />
      <EntityResultsDownloadModal
        open={isResultsDownloadModalOpen}
        onClose={() => setIsResultsDownloadModalOpen(false)}
        databaseId={databaseId}
        databaseName={suppliersData?.database_name}
        entityId={entityIdForHitsReport}
        entityName={entity}
        buildEntityScreeningReport={buildEntityScreeningReport}
        groupId={groupId}
        canAccessVerificationResults={canAccessVerificationResults}
        hasVerificationResults={hasVerificationResults}
        onViewReports={handleViewReports}
      />
      <BulkDismissHitsModal
        open={isBulkReviewOpen}
        selections={bulkSelections}
        isProcessing={isBulkHiding}
        onClose={() => setIsBulkReviewOpen(false)}
        onRemove={handleRemoveBulkSelection}
        onConfirm={handleConfirmBulkUpdate}
        operation={bulkOperation || 'hide'}
      />
    </div>
  );
};

export default LensesTable;
